import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { LabelSettingsLabel, LabelSettingsView } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  createLabel,
  labelSettingsQuery,
  privacyKeys,
  setLabelRoles,
  updateLabel,
} from '../api/privacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import styles from './privacy.module.css';

// Settings > Sensitivity labels (docs/plans/golive-privacy-tooling.md §1.4): the labels that
// keep sensitive visits to the roles cleared for them, and which roles those are. A label's
// code never changes; a label is switched off, never deleted, and visits keep it.

const CODE_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function useSave() {
  const queryClient = useQueryClient();
  return (view: LabelSettingsView) => {
    queryClient.setQueryData(privacyKeys.labels, view);
  };
}

function LabelDialog({
  label,
  onClose,
}: {
  /** Null to add a new label. */
  label: LabelSettingsLabel | null;
  onClose: () => void;
}) {
  const save = useSave();
  const online = useOnline();
  const [code, setCode] = useState(label?.code ?? '');
  const [display, setDisplay] = useState(label?.display ?? '');
  const [description, setDescription] = useState(label?.description ?? '');
  const [status, setStatus] = useState<LabelSettingsLabel['status']>(label?.status ?? 'active');
  const codeValid = label !== null || CODE_PATTERN.test(code);
  const ready = codeValid && display.trim() !== '' && display.trim().length <= 80;
  const mutation = useMutation({
    mutationFn: () =>
      label === null
        ? createLabel({
            code,
            display: display.trim(),
            ...(description.trim() === '' ? {} : { description: description.trim() }),
          })
        : updateLabel(label.id, {
            display: display.trim(),
            description: description.trim() === '' ? null : description.trim(),
            status,
          }),
    onSuccess: (view) => {
      save(view);
      toasts.show({ title: label === null ? 'Label added' : 'Label saved' });
      onClose();
    },
  });
  return (
    <Dialog
      title={label === null ? 'Add label' : `Label ${label.display}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {label === null ? (
        <TextField
          label="Code"
          description="Lower-case letters, digits and underscores. It can't be changed later."
          width="md"
          value={code}
          onChange={setCode}
          {...(code === '' || codeValid
            ? {}
            : { errorMessage: 'Start with a letter; use a-z, 0-9 and _ only.' })}
        />
      ) : (
        <p className={styles.meta}>
          Code <span className={styles.mono}>{label.code}</span>
        </p>
      )}
      <TextField label="Name" value={display} onChange={setDisplay} />
      <TextArea
        label="What it is for"
        description="Shown to whoever puts it on a visit."
        rows={3}
        value={description}
        onChange={setDescription}
      />
      {label === null ? null : (
        <Select
          label="State"
          description="A label not in use can't be put on a visit. Visits that carry it keep it."
          options={[
            { id: 'active', label: 'In use' },
            { id: 'inactive', label: 'Not in use' },
          ]}
          value={status}
          onChange={(v) => {
            if (v === 'active' || v === 'inactive') setStatus(v);
          }}
        />
      )}
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The label was not saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready || !online}
          isPending={mutation.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mutation.mutate();
          }}
        >
          {label === null ? 'Add label' : 'Save label'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RolesDialog({
  label,
  roles,
  onClose,
}: {
  label: LabelSettingsLabel;
  roles: LabelSettingsView['roles'];
  onClose: () => void;
}) {
  const save = useSave();
  const online = useOnline();
  const [chosen, setChosen] = useState(() => new Set(label.roles.map((r) => r.id)));
  const mutation = useMutation({
    mutationFn: () => setLabelRoles(label.id, [...chosen]),
    onSuccess: (view) => {
      save(view);
      toasts.show({ title: 'Roles saved' });
      onClose();
    },
  });
  return (
    <Dialog
      title={`Roles cleared for ${label.display}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        People in these roles see visits with this label. Everyone else sees them as not found, or
        as a restricted record where care or payment would otherwise stop.
      </p>
      <div className={styles.checks} role="group" aria-label="Roles">
        {roles.map((role) => (
          <Checkbox
            key={role.id}
            isSelected={chosen.has(role.id)}
            onChange={(selected) => {
              setChosen((current) => {
                const next = new Set(current);
                if (selected) next.add(role.id);
                else next.delete(role.id);
                return next;
              });
            }}
          >
            {role.name}
          </Checkbox>
        ))}
      </div>
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The roles were not saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={mutation.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mutation.mutate();
          }}
        >
          Save roles
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Labels({ view, mayGrant }: { view: LabelSettingsView; mayGrant: boolean }) {
  const online = useOnline();
  const [editing, setEditing] = useState<LabelSettingsLabel | 'new' | null>(null);
  const [granting, setGranting] = useState<LabelSettingsLabel | null>(null);
  // TODO(clinical-verify) SL-1: the clinical safety lead confirms the labels and grants.
  return (
    <Section
      title="Labels"
      description="Provisional until the clinical safety lead confirms the labels and who is cleared for each."
      actions={
        <Button
          isDisabled={!online}
          onPress={() => {
            setEditing('new');
          }}
        >
          Add label
        </Button>
      }
    >
      {view.labels.length === 0 ? (
        <p className={styles.meta}>No labels yet. Add one for each kind of sensitive care.</p>
      ) : (
        <TableScroll>
          <Table aria-label="Sensitivity labels" flat>
            <TableHeader>
              <Column isRowHeader>Name</Column>
              <Column>Code</Column>
              <Column>State</Column>
              <Column>Roles cleared</Column>
              <Column>Actions</Column>
            </TableHeader>
            <TableBody items={view.labels}>
              {(l) => (
                <Row id={l.id}>
                  <Cell>{l.display}</Cell>
                  <Cell>
                    <span className={styles.mono}>{l.code}</span>
                  </Cell>
                  <Cell>
                    <Tag state={l.status === 'active' ? 'active' : 'neutral'}>
                      {l.status === 'active' ? 'In use' : 'Not in use'}
                    </Tag>
                  </Cell>
                  <Cell>
                    {l.roles.length === 0 ? 'None' : l.roles.map((r) => r.name).join(', ')}
                  </Cell>
                  <Cell>
                    <div className={styles.actions}>
                      <Button
                        dense
                        variant="quiet"
                        isDisabled={!online}
                        onPress={() => {
                          setEditing(l);
                        }}
                      >
                        Change
                      </Button>
                      {mayGrant ? (
                        <Button
                          dense
                          variant="quiet"
                          isDisabled={!online}
                          onPress={() => {
                            setGranting(l);
                          }}
                        >
                          Roles cleared
                        </Button>
                      ) : null}
                    </div>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      )}
      {editing === null ? null : (
        <LabelDialog
          label={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {granting === null ? null : (
        <RolesDialog
          label={granting}
          roles={view.roles}
          onClose={() => {
            setGranting(null);
          }}
        />
      )}
    </Section>
  );
}

export function LabelsSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'sensitivity_labels.manage');
  const settings = useQuery({ ...labelSettingsQuery, enabled: allowed });
  if (!allowed)
    return (
      <SettingsPage title="Sensitivity labels">
        <Notice tone="info">You don&apos;t have permission to manage sensitivity labels.</Notice>
      </SettingsPage>
    );
  return (
    <SettingsPage title="Sensitivity labels">
      {settings.isPending ? <LoadingBlock label="Loading the labels" rows={3} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void settings.refetch()}>Try again</Button>}
        >
          {errorText(settings.error, 'The labels could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {settings.isSuccess ? (
        <Labels view={settings.data} mayGrant={can(session, 'admin.roles.manage')} />
      ) : null}
    </SettingsPage>
  );
}
